import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { getTodayDateString } from '@/lib/utils';
import { useCreateJobCost } from '../hooks/useCreateJobCost';
import { useUpdateJobCost } from '../hooks/useUpdateJobCost';
import { useCostCategories } from '../hooks/useCostCategories';
import { CostImagesSection } from './CostImagesSection';
import type { JobCost } from '../types/jobCost';

interface CostEntryFormProps {
  jobId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingCost?: JobCost | null;
  isForecast: boolean;
}

export function CostEntryForm({
  jobId,
  isOpen,
  onClose,
  onSuccess,
  editingCost,
  isForecast,
}: CostEntryFormProps) {
  const { data: categories } = useCostCategories();
  const { create, isCreating, error: createError } = useCreateJobCost();
  const { update, isUpdating, error: updateError } = useUpdateJobCost();

  // Form state
  const [costCategoryId, setCostCategoryId] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [paidTo, setPaidTo] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const [taxAmountManuallyEdited, setTaxAmountManuallyEdited] = useState(false);
  const [validationError, setValidationError] = useState('');

  const error = createError || updateError || validationError;
  const isSubmitting = isCreating || isUpdating;

  // Initialize form when dialog opens or editingCost changes
  useEffect(() => {
    // This effect intentionally sets state to initialize/reset the form based on props.
    // This is a valid use case for form initialization and doesn't cause cascading renders.
    if (isOpen) {
      if (editingCost) {
        // Populate form with existing cost data
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCostCategoryId(editingCost.costCategoryId.toString());

        setPaymentDate(editingCost.paymentDate.split('T')[0]);

        setCheckNumber(editingCost.checkNumber || '');

        setPaidTo(editingCost.paidTo);

        setDescription(editingCost.description);

        setAmount(editingCost.amount.toString());

        setTaxRate((editingCost.taxRate * 100).toString());

        setTaxAmount(editingCost.taxAmount.toString());

        setTaxAmountManuallyEdited(false);
      } else {
        // Reset form for new cost
        setCostCategoryId('');
        setPaymentDate(getTodayDateString());
        setCheckNumber('');
        setPaidTo('');
        setDescription('');
        setAmount('');
        setTaxRate('8.25'); // default tax rate
        setTaxAmount('');
        setTaxAmountManuallyEdited(false);
      }

      setValidationError('');
    }
  }, [isOpen, editingCost]);

  // Auto-calculate tax amount when amount or tax rate changes
  useEffect(() => {
    // This effect intentionally sets state to auto-calculate tax based on amount and rate.
    // This is a valid use case for derived state calculation.
    if (!taxAmountManuallyEdited) {
      const amountNum = parseFloat(amount);
      const rateNum = parseFloat(taxRate);
      if (!isNaN(amountNum) && !isNaN(rateNum) && amountNum > 0 && rateNum >= 0) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setTaxAmount((amountNum * (rateNum / 100)).toFixed(2));
      } else {
        setTaxAmount('0');
      }
    }
  }, [amount, taxRate, taxAmountManuallyEdited]);

  // Get the current tax amount (either manual or calculated)
  const calculatedTaxAmount = taxAmount || '0';

  // Update tax rate when category changes
  const handleCategoryChange = (value: string) => {
    setCostCategoryId(value);

    const category = categories.find((c) => c.id.toString() === value);
    if (category?.applyTaxByDefault) {
      setTaxRate('8.25'); // Default tax rate
    } else {
      setTaxRate('0');
    }
    // Reset manual edit flag so auto-calculation kicks in
    setTaxAmountManuallyEdited(false);
  };

  // Handle manual tax amount change
  const handleTaxAmountChange = (value: string) => {
    setTaxAmount(value);
    setTaxAmountManuallyEdited(true);
  };

  const validateForm = (): boolean => {
    if (!costCategoryId) {
      setValidationError('Category is required');
      return false;
    }

    if (!paymentDate) {
      setValidationError('Payment date is required');
      return false;
    }

    if (!paidTo.trim()) {
      setValidationError('Paid To is required');
      return false;
    }

    if (paidTo.length > 100) {
      setValidationError('Paid To must be 100 characters or less');
      return false;
    }

    if (!description.trim()) {
      setValidationError('Description is required');
      return false;
    }

    if (description.length > 500) {
      setValidationError('Description must be 500 characters or less');
      return false;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setValidationError('Amount must be greater than 0');
      return false;
    }

    const rateNum = parseFloat(taxRate);
    if (isNaN(rateNum) || rateNum < 0 || rateNum > 100) {
      setValidationError('Tax rate must be between 0 and 100');
      return false;
    }

    const taxNum = parseFloat(calculatedTaxAmount);
    if (isNaN(taxNum) || taxNum < 0) {
      setValidationError('Tax amount must be 0 or greater');
      return false;
    }

    if (checkNumber && checkNumber.length > 50) {
      setValidationError('Check number must be 50 characters or less');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (!validateForm()) {
      return;
    }

    const data = {
      costCategoryId: parseInt(costCategoryId, 10),
      isForecast,
      paymentDate: new Date(paymentDate).toISOString(),
      checkNumber: checkNumber.trim() || undefined,
      paidTo: paidTo.trim(),
      description: description.trim(),
      amount: parseFloat(amount),
      taxRate: parseFloat(taxRate) / 100, // Convert percentage to decimal
      taxAmount: parseFloat(calculatedTaxAmount),
    };

    let result;
    if (editingCost) {
      result = await update(jobId, editingCost.id, data);
    } else {
      result = await create(jobId, data);
    }

    if (result) {
      onSuccess();
      onClose();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingCost ? 'Edit Cost' : `Add ${isForecast ? 'Forecasted' : 'Actual'} Cost`}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <ErrorAlert error={error} />

          <div className="space-y-2">
            <Label htmlFor="category">
              Category <span className="text-red-500">*</span>
            </Label>
            <Select
              id="category"
              value={costCategoryId}
              onChange={(e) => handleCategoryChange(e.target.value)}
              required
            >
              <option value="">Select category</option>
              {categories
                .filter((c) => c.isActive)
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((category) => (
                  <option key={category.id} value={category.id.toString()}>
                    {category.name}
                  </option>
                ))}
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="paymentDate">
                Payment Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="paymentDate"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="checkNumber">Check Number</Label>
              <Input
                id="checkNumber"
                type="text"
                placeholder="Optional"
                value={checkNumber}
                onChange={(e) => setCheckNumber(e.target.value)}
                maxLength={50}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="paidTo">
              Paid To <span className="text-red-500">*</span>
            </Label>
            <Input
              id="paidTo"
              type="text"
              placeholder="Company or person name"
              value={paidTo}
              onChange={(e) => setPaidTo(e.target.value)}
              maxLength={100}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              placeholder="Enter cost description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              required
            />
            <p className="text-xs text-gray-500">{description.length} / 500 characters</p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">
                Amount <span className="text-red-500">*</span>
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="taxRate">Tax Rate %</Label>
              <Input
                id="taxRate"
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="0.00"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="taxAmount">Tax Amount</Label>
              <Input
                id="taxAmount"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={calculatedTaxAmount}
                onChange={(e) => handleTaxAmountChange(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : editingCost ? 'Update Cost' : 'Add Cost'}
            </Button>
          </div>

          {/* Image section - only show when editing an existing cost */}
          {editingCost && (
            <>
              <div className="border-t border-gray-200 my-4 pt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Receipt Images</h3>
                <CostImagesSection jobId={jobId} costId={editingCost.id} canDelete={true} />
              </div>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
